/**
 * Header - Page Header Component
 *
 * Dark header component following Challenge design system.
 * Includes brand logo, points/level display, Live/Info icons,
 * countdown timer via useCountdown hook (V5.9), and login/user state.
 *
 * useCountdown(targetDate) returns {days, hours, minutes, seconds} or null,
 * updating every second. Timer position (left/center/right) is configurable
 * via navbarConfig.timerPosition.
 */

import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import classNames from "classnames";
import DevourTopNavUserData from "@/components/DevourTopNavUserData";
import { FaVideo, FaInfoCircle, FaGift } from "react-icons/fa";
import { IoMdPersonAdd } from "react-icons/io";
import "./Header.scss";

interface HeaderProps {
    hubName: string;
    logoUrl?: string;
    navbarConfig?: {
        backgroundColor?: string;
        textColor?: string;
        iconColor?: string;
        borderColor?: string;
        borderVisible?: boolean;
        showTimer?: boolean;
        timerPosition?: "left" | "center" | "right";
        timerColor?: string;
        countdownTargetDate?: number;
        accentBarGradient?: string;
        customAvatarImageUrl?: string;
    };
    /** Called when an unauthenticated user attempts an auth-gated action */
    onLoginRequired: () => void;
    /** _id of the streaming HubSection — used to scroll to it and highlight the Live icon */
    streamingSectionId?: string;
    hasLiveStreams?: boolean;
    onScrollToStreaming?: () => void;
    infoSectionId?: string;
    activeSection?: string | null;
    onSectionChange?: (sectionId: string | null) => void;
    // Points / Level display
    pointsName?: string;
    pointsIconUrl?: string;
    currentPoints?: number;
    currentLevel?: number;
    /** Points threshold for next level — denominator for mini progress bar */
    nextLevelPoints?: number;
    themeColors?: {
        primary?: string;
        accent?: string;
    };
    onViewRewards?: () => void;
    onInviteClick?: () => void;
}

/**
 * Countdown timer hook — returns days/hours/minutes/seconds until target date.
 * Updates every second. Returns null if no target date or target is in the past.
 */
function useCountdown(targetDate?: number): { days: number; hours: number; minutes: number; seconds: number } | null {
    const [timeLeft, setTimeLeft] = useState<{ days: number; hours: number; minutes: number; seconds: number } | null>(null);

    useEffect(() => {
        if (!targetDate) {
            setTimeLeft(null);
            return;
        }

        function calculate() {
            const diff = targetDate! - Date.now();
            if (diff <= 0) {
                setTimeLeft(null);
                return;
            }
            const days = Math.floor(diff / 86400000);
            const hours = Math.floor(diff % 86400000 / 3600000);
            const minutes = Math.floor(diff % 3600000 / 60000);
            const seconds = Math.floor(diff % 60000 / 1000);
            setTimeLeft({ days, hours, minutes, seconds });
        }

        calculate();
        const interval = setInterval(calculate, 1000);
        return () => clearInterval(interval);
    }, [targetDate]);

    return timeLeft;
}

export const Header = ({
    hubName,
    logoUrl,
    navbarConfig,
    onLoginRequired,
    streamingSectionId,
    hasLiveStreams = false,
    onScrollToStreaming,
    infoSectionId,
    activeSection,
    onSectionChange,
    pointsName = "Beast Points",
    pointsIconUrl,
    currentPoints = 0,
    currentLevel = 1,
    nextLevelPoints = 250,
    themeColors,
    onViewRewards,
    onInviteClick,
}: HeaderProps) => {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const countdown = useCountdown(navbarConfig?.showTimer ? navbarConfig.countdownTargetDate : undefined);

    // Build header style from navbarConfig
    const headerStyle: React.CSSProperties = {
        ...navbarConfig?.backgroundColor ? { backgroundColor: navbarConfig.backgroundColor } : {},
        ...navbarConfig?.textColor ? { color: navbarConfig.textColor } : {},
        ...navbarConfig?.borderVisible === false ? { borderBottom: "none" } : {},
        ...navbarConfig?.borderColor ? { borderBottomColor: navbarConfig.borderColor } : {},
    };

    const iconColor = navbarConfig?.iconColor || navbarConfig?.textColor;
    const timerColor = navbarConfig?.timerColor || navbarConfig?.textColor;

    // Calculate progress percentage for mini progress bar (guard against zero denominator)
    const progressPercent = nextLevelPoints > 0
        ? Math.min(currentPoints / nextLevelPoints * 100, 100)
        : 100;
    // Use brand's theme color - accent first, then primary
    const accentColor = themeColors?.accent || themeColors?.primary;

    /** Countdown timer element — positioned per navbarConfig.timerPosition */
    const timerElement = countdown && navbarConfig?.showTimer
        ? <div className="header__timer" style={timerColor ? { color: timerColor } : undefined}>
            <strong>{countdown.days}</strong>d{" "}
            <strong>{countdown.hours}</strong>h{" "}
            <strong>{countdown.minutes}</strong>m{" "}
            <strong>{countdown.seconds}</strong>s
        </div>
        : null;

    const timerPosition = navbarConfig?.timerPosition || "center";

    return (
        <>
            <header className="header" style={headerStyle}>
                {/* Logo / Name - Clickable to return to main view */}
                <button
                    className="header__logo"
                    onClick={() => {
                        onSectionChange?.(null);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    title={`Return to ${hubName} home`}
                >
                    {logoUrl
                        ? <img
                            src={logoUrl}
                            alt={hubName}
                            className="header__logo-img"
                            onError={(e) => {
                                (e.target as HTMLImageElement).style.display = "none";
                            }}
                        />
                        : <span className="header__logo-text">{hubName}</span>
                    }
                </button>

                {/* Timer — left position (after logo) */}
                {timerPosition === "left" && timerElement}

                {/* Points / Level Stats - Only when logged in */}
                {fullToken &&
                <div className="header__stats">
                    {/* Level Badge */}
                    <div
                        className="header__level-badge"
                        style={{ backgroundColor: accentColor }}
                    >
                        <span className="header__level-number">{currentLevel}</span>
                    </div>

                    {/* Points Info with Mini Progress */}
                    <div className="header__points-info">
                        <div className="header__points-row">
                            {pointsIconUrl &&
                                <img
                                    src={pointsIconUrl}
                                    alt=""
                                    className="header__points-icon"
                                />
                            }
                            <span className="header__points-value">
                                {currentPoints.toLocaleString()}
                            </span>
                            <span
                                className="header__points-name"
                                style={{ color: accentColor }}
                            >
                                {pointsName}
                            </span>
                        </div>
                        <div className="header__progress-bar">
                            <div
                                className="header__progress-fill"
                                style={{ width: `${progressPercent}%`, backgroundColor: accentColor }}
                            />
                        </div>
                    </div>

                    {/* Gift/Rewards Button */}
                    {onViewRewards &&
                        <button
                            className="header__rewards-btn"
                            onClick={onViewRewards}
                            title="View Rewards"
                            style={{ color: accentColor }}
                        >
                            <FaGift />
                        </button>
                    }
                </div>
                }

                {/* Timer — center position (between stats and right icons) */}
                {timerPosition === "center" && timerElement}

                {/* Right side: Section icons + Login/User */}
                <div className="header__right">
                    {/* Timer — right position (within right group) */}
                    {timerPosition === "right" && timerElement}

                    {/* Live (Streaming) Icon - Shows when creators are live */}
                    {hasLiveStreams &&
                    <button
                        className={classNames("header__icon-btn", "header__icon-btn--live", {
                            "header__icon-btn--active": activeSection === streamingSectionId,
                        })}
                        onClick={() => {
                            if (onScrollToStreaming) {
                                onScrollToStreaming();
                            } else if (streamingSectionId) {
                                onSectionChange?.(streamingSectionId);
                            }
                        }}
                        title="Live Now"
                        style={iconColor ? { color: iconColor } : undefined}
                    >
                        <FaVideo size={20} />
                    </button>
                    }

                    {/* Info Icon */}
                    {infoSectionId &&
                    <button
                        className={classNames("header__icon-btn", {
                            "header__icon-btn--active": activeSection === infoSectionId,
                        })}
                        onClick={() => onSectionChange?.(infoSectionId)}
                        title="Info"
                        style={iconColor ? { color: iconColor } : undefined}
                    >
                        <FaInfoCircle size={20} />
                    </button>
                    }

                    {/* Invite Friend Icon — only for logged-in users */}
                    {fullToken && onInviteClick &&
                    <button
                        className="header__icon-btn"
                        onClick={onInviteClick}
                        title="Invite Friends"
                        style={iconColor ? { color: iconColor } : undefined}
                    >
                        <IoMdPersonAdd size={20} />
                    </button>
                    }

                    {/* User / Login */}
                    {fullToken
                        ? <DevourTopNavUserData
                            onShowSignInToast={() => {}}
                            dropdownAnchorElement=".header"
                            avatarOnly
                            hideWalletOnMobile={true}
                            hideChallengeSiteMenuItems={true}
                            customAvatarUrl={navbarConfig?.customAvatarImageUrl}
                        />
                        : <button
                            type="button"
                            className="btn btn--ghost header__login"
                            onClick={onLoginRequired}
                            style={navbarConfig?.textColor ? { color: navbarConfig.textColor, borderColor: navbarConfig.textColor } : undefined}
                        >
                            <span>Login</span>
                        </button>
                    }
                </div>
            </header>
            {navbarConfig?.accentBarGradient &&
            <div
                className="header__accent-bar"
                style={{ background: navbarConfig.accentBarGradient }}
            />
            }
        </>
    );
};
